/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The top bar's Refresh, as in the Ops Console: it asks the page on screen to reload its data
 * rather than reloading the browser (which would lose open modals and form input).
 *
 * A page listens with `window.addEventListener(SWIFT_REFRESH_EVENT, handler)` (or `onSwiftRefresh`,
 * which returns the unsubscribe). Only when no page is listening does Refresh fall back to
 * `location.reload()`. Listeners added with plain `window.addEventListener` are counted too: the
 * two window methods are wrapped for this one event type only, and pass everything through.
 */
export const SWIFT_REFRESH_EVENT = "swift:refresh";

type Listener = EventListenerOrEventListenerObject;
const listeners = new Set<Listener>();

declare global {
  interface Window {
    __swiftRefreshTracked?: boolean;
  }
}

const track = () => {
  if (typeof window === "undefined" || window.__swiftRefreshTracked) return;
  window.__swiftRefreshTracked = true;
  const add = window.addEventListener.bind(window);
  const remove = window.removeEventListener.bind(window);
  window.addEventListener = ((type: string, listener: Listener, options?: any) => {
    if (type === SWIFT_REFRESH_EVENT && listener) listeners.add(listener);
    add(type, listener, options);
  }) as typeof window.addEventListener;
  window.removeEventListener = ((type: string, listener: Listener, options?: any) => {
    if (type === SWIFT_REFRESH_EVENT && listener) listeners.delete(listener);
    remove(type, listener, options);
  }) as typeof window.removeEventListener;
};
track();

export const hasRefreshListeners = () => listeners.size > 0;

export const onSwiftRefresh = (handler: (event: Event) => void) => {
  window.addEventListener(SWIFT_REFRESH_EVENT, handler);
  return () => window.removeEventListener(SWIFT_REFRESH_EVENT, handler);
};

/**
 * Asks the page to reload its data. Returns "event" when a page handled it, "reload" when the
 * browser was reloaded instead. `reload` is replaceable for tests.
 */
export const requestRefresh = (reload: () => void = () => window.location.reload()) => {
  const event = new Event(SWIFT_REFRESH_EVENT, { cancelable: true });
  const listening = hasRefreshListeners();
  const notPrevented = window.dispatchEvent(event);
  if (listening || !notPrevented) return "event";
  reload();
  return "reload";
};
